import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './header.scss';
import { t } from '../../services/i18nService';
import connectionStatusConstants from '../../constants/ConnectionStatusConstants';
import utilService from '../../services/UtilService';
import sessionReader from '../../store/readHelpers/SessionReader';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../services/a11yIIBScreenReaderService';

const {
    STARTED_PRELOADING,
    RECONNECTING,
    CONNECTION_RESTORED,
    SAVED,
    DISCONNECTED,
    OFFLINE_MODE,
    SERVER_ERROR,
    SYNCHRONISING,
} = connectionStatusConstants;

/**
 * Returns true when the keyboard event matches the Ctrl+Alt+S shortcut
 * used to focus the Wi-Fi connection status icon on demand.
 * Exported as a pure function for unit testing.
 */
export const isCtrlAltSShortcut = event =>
    !!(event && event.ctrlKey && event.altKey && event.key.toLowerCase() === 's');

// Announcement tier map for v5: null = silent, 'polite' = queued, 'alert' = immediate interruption.
const CONNECTION_STATUS_ANNOUNCEMENT_TIER_MAP = {
    [CONNECTION_RESTORED]: 'polite',
    [DISCONNECTED]: 'alert',
    [OFFLINE_MODE]: 'alert',
    [SERVER_ERROR]: 'alert',
};

/**
 * Returns a keydown handler that focuses the Wi-Fi icon on Ctrl+Alt+S.
 * Defined outside the component so it is never recreated on re-renders.
 */
export const createKeyDownHandler = iconRef => (event) => {
    if (isCtrlAltSShortcut(event)) {
        event.preventDefault();
        if (iconRef.current) {
            iconRef.current.focus();
        }
    }
};

const ConnectionStatus = React.memo(({ connectionStatus, offlineMode }) => {
    const iconRef = useRef(null);

    let connectedStatusText;
    let isDisconnected = false;
    let connectionIcon = 'fa-wifi';
    let assertive = false;
    let srOnly = false;

    if (!utilService.isDevelopmentQueryParameterTrue('hideConnectionStatus')) {
        switch (connectionStatus) {
        case STARTED_PRELOADING:
            connectedStatusText = t('status.startedPreloading');
            break;

        case RECONNECTING:
            assertive = true;
            connectionIcon = 'fa-refresh fa-spin';
            connectedStatusText = t('status.restoringConnectionText');
            break;

        case CONNECTION_RESTORED:
            assertive = true;
            connectedStatusText = t('status.connectionRestored');
            break;

        case SAVED:
            connectedStatusText = t('status.saved');
            break;

        case DISCONNECTED:
            isDisconnected = true;
            assertive = true;
            connectedStatusText = sessionReader.isBackgroundAudioControlAllowed()
                ? t('status.audioControlConnectionLost') : t('status.connectionLost');
            break;

        case OFFLINE_MODE:
            connectionIcon = 'fa-hdd-o';
            connectedStatusText = t('status.offlinemode');
            break;

        case SERVER_ERROR:
            isDisconnected = true;
            connectedStatusText = t('status.serverProblem');
            break;

        case SYNCHRONISING:
            connectedStatusText = t('status.synchronising');
            break;

        default:
            connectedStatusText = t('status.connected');
            srOnly = true;
            break;
        }
    }

    const showDisconnectedIcon = !offlineMode && isDisconnected;
    const isV5Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
        A11yForIIBScreenReaderIteration.v5
    );

    // Ctrl+Alt+S: focuses the Wi-Fi icon for on-demand SR reading (Tier 1, no live region push). No-op in non-v5.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isV5Supported is stable (service call, not state/props); [] is intentional.
    useEffect(() => {
        if (!isV5Supported) {
            return undefined;
        }
        const handler = createKeyDownHandler(iconRef);
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    const renderV5 = () => {
        // v5: Tier 1=silent, Tier 2=polite (CONNECTION_RESTORED), Tier 3=alert (DISCONNECTED/OFFLINE_MODE/SERVER_ERROR).
        const announcementTier = CONNECTION_STATUS_ANNOUNCEMENT_TIER_MAP[connectionStatus] || null;
        const ariaLiveProps = announcementTier === 'alert' ? { role: 'alert' } : { 'aria-live': 'polite' };

        return (
            <div className={styles.connectionStatusContainer}>
                <div className={styles.connectionStatus}>
                    {/* SR live region — always in DOM; content only set for Tier 2/3 states. */}
                    <div
                        className="sr-only"
                        {...ariaLiveProps}
                    >
                        { announcementTier ? connectedStatusText : null }
                    </div>
                    {/* Visual text — aria-hidden so SR reads exclusively from the live region above. */}
                    { connectedStatusText && (
                        <span aria-hidden="true" className={srOnly ? 'sr-only' : ''}>
                            { connectedStatusText }
                        </span>
                    )}
                </div>
                <div
                    ref={iconRef}
                    tabIndex={0}
                    className={styles.icon + (isDisconnected ? ` ${styles.disconnected}` : '')}
                    aria-label={connectedStatusText || ''}
                >
                    <i className={`fa ${connectionIcon}`} aria-hidden="true" />
                    { showDisconnectedIcon && <i className={`${styles.disconnectedIcon} fa fa-times-circle`} aria-hidden="true" /> }
                </div>
            </div>
        );
    };

    if (isV5Supported) {
        return renderV5();
    }

    return (
        <div className={styles.connectionStatusContainer}>
            <div aria-live={assertive ? 'assertive' : 'polite'} className={styles.connectionStatus}>
                { connectedStatusText && <span role="status" className={srOnly ? 'sr-only' : ''}>{ connectedStatusText }</span> }
            </div>
            <div className={styles.icon + (isDisconnected ? ` ${styles.disconnected}` : '')}>
                <i className={`fa ${connectionIcon}`} aria-hidden="true" />
                { showDisconnectedIcon && <i className={`${styles.disconnectedIcon} fa fa-times-circle`} aria-hidden="true" /> }
            </div>
        </div>
    );
});

ConnectionStatus.propTypes = {
    connectionStatus: PropTypes.string,
    offlineMode: PropTypes.bool,
};

export default ConnectionStatus;
